<template>
  <div class="feed-entry-item">
    <div class="_top">
      <div class="_user">
        <feed-entry-icon :feed-entry="feedEntry" />
      </div>
      <div class="_right">
        <div class="_title">
          <feed-entry-title :feed-entry="feedEntry" />
        </div>
        <div class="_date">
          {{ formatDate(feedEntry.createdAt) }}
        </div>
      </div>
      <div class="_indicator">
        <div
          v-if="feedEntry.readAt === null"
          class="_circle"
        />
      </div>
    </div>
    <div
      v-if="hasPayload"
      class="_payload"
    >
      <notification-comment
        v-if="feedEntry.comment !== null"
        :feed-entry="feedEntry"
      />
      <template v-else-if="feedEntry.update !== null">
        <div class="_title">
          {{ displayNameOf(feedEntry.update.creator, $t('user.deleted.name')) }}
        </div>
        <update-feed-item
          :update="feedEntry.update"
          :level="0"
          hide-props
          hide-top
          :editor-font-size="fontSizes[4]"
        />
      </template>
      <data-source-query-error
        v-else-if="feedEntry.dataSourceQueryError !== null"
        :data-source="feedEntry.dataSource"
        :error="feedEntry.dataSourceQueryError"
      />
    </div>
    <div
      v-if="hasAction"
      class="_payload"
    >
      <div style="display: inline-flex;">
        <template
          v-if="!isFulfilled"
        >
          <m-btn
            v-for="(action, index) in actions"
            :key="index"
            :to="action.to"
            class="_action"
            @click="handleFeedEntryClick(action)"
          >
            <m-icon
              v-if="action.icon"
              :type="action.icon"
              class="_icon"
            />
            {{ action.title }}
          </m-btn>
        </template>
        <div
          v-else
          class="_fulfilled-msg"
        >
          <div class="_checkmark">
            <m-icon type="check-circle" />
          </div>
          <div>
            {{ $t(`notificationFeed.actionCompleted.${camelCase(feedEntry.type)}`) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import DataSourceQueryError from '@/components/datasource/DataSourceQueryError.vue';
import FeedEntryIcon from '@/components/inbox/FeedEntryIcon.vue';
import FeedEntryTitle from '@/components/inbox/FeedEntryTitle.vue';
import NotificationComment from '@/components/inbox/NotificationComment.vue';
import UpdateFeedItem from '@/components/updates/UpdateFeedItem.vue';
import { DateTime } from 'luxon';
import { GOAL_DETAILS, UPDATES_EXPLORER } from '@/route-names';
import { camelCase } from 'lodash-es';
import { connectUrl, dataSourceSignInIcon } from '@/lib/data-source';
import { dataSourceInstallStatus } from '@/constants/bumblebee.json';
import { displayName as displayNameOf } from 'shared/composables/user';
import { downloadFromURL } from 'shared/lib/file-download';
import { feedEntryType } from 'shared/constants.json';
import { fontSizes } from 'shared/font-sizes';
import { initOAuthInstall } from '@/lib/oauth-redirect';

export default {
  name: 'FeedEntryItem',
  props: {
    feedEntry: {
      type: Object,
      required: true,
    },
  },
  components: {
    DataSourceQueryError,
    NotificationComment,
    UpdateFeedItem,
    FeedEntryIcon,
    FeedEntryTitle,
  },
  setup() {
    return { displayNameOf };
  },
  data() {
    return {
      feedEntryType,
      camelCase,
      fontSizes,
    };
  },
  computed: {
    hasPayload() {
      const hasPayload = [
        feedEntryType.goalUpdate,
        feedEntryType.goalComment,
        feedEntryType.goalUpdateMention,
        feedEntryType.updateMention,
        feedEntryType.updateComment,
        feedEntryType.goalUpdateComment,
      ].includes(this.feedEntry.type);

      const hasPayloadIf = feedEntryType.dataSourceQueryError === this.feedEntry.type && !this.isFulfilled;

      return hasPayload || hasPayloadIf;
    },
    payload() {
      return this.feedEntry.update.message;
    },
    hasAction() {
      return [
        feedEntryType.updateNotification,
        feedEntryType.dataSourceUnhealthy,
        feedEntryType.dataSourceDead,
        feedEntryType.dataSourceQueryError,
        feedEntryType.reportReady,
      ].includes(this.feedEntry.type);
    },
    isFulfilled() {
      return this.feedEntry.fulfilledAt !== null;
    },
    actions() {
      if ([feedEntryType.updateNotification, feedEntryType.dataSourceQueryError].includes(this.feedEntry.type)) {
        return [
          {
            to: this.feedEntryAction,
            title: this.$t(`notificationFeed.action.${camelCase(this.feedEntry.type)}`),
          },
        ];
      }

      if ([feedEntryType.dataSourceUnhealthy, feedEntryType.dataSourceDead].includes(this.feedEntry.type)) {
        return [
          {
            onClick: this.refreshConnection(this.feedEntry.dataSource, this.feedEntry),
            title: this.$t(`notificationFeed.action.${camelCase(this.feedEntry.type)}`),
            icon: dataSourceSignInIcon(this.feedEntry.dataSource),
          },
        ];
      }
      if ([feedEntryType.reportReady].includes(this.feedEntry.type)) {
        return [
          {
            onClick: this.downloadFile(this.feedEntry.printable.storageObject),
            title: this.$t(`notificationFeed.action.${camelCase(this.feedEntry.type)}`),
          },
        ];
      }

      return [];
    },
    feedEntryAction() {
      switch (this.feedEntry.type) {
        case feedEntryType.updateNotification:
          if (this.feedEntry.notification.updateTemplate === null) {
            return {
              name: UPDATES_EXPLORER,
              query: { create: '', notificationId: this.feedEntry.notification.uid },
            };
          }
          return {
            name: UPDATES_EXPLORER,
            query: {
              create: '',
              templateId: this.feedEntry.notification.updateTemplate.uid,
              notificationId: this.feedEntry.notification.uid,
            },
          };
        case feedEntryType.dataSourceQueryError:
          return {
            name: GOAL_DETAILS,
            params: { goalId: this.feedEntry.goal.uid },
          };
        default:
          return null;
      }
    },
  },
  methods: {
    formatDate(date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED);
    },
    refreshConnection(dataSource) {
      return () => {
        initOAuthInstall(connectUrl(dataSource)).then((event) => {
          switch (event.data.status) {
            case dataSourceInstallStatus.error:
            case dataSourceInstallStatus.notAllowed:
              this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
              break;
            case dataSourceInstallStatus.success:
              this.$showSnackbar({ color: 'success', message: this.$t('dataSource.successfullySynced') });
              break;
            default:
              break;
          }
        });
      };
    },
    downloadFile(storageObject) {
      return () => {
        downloadFromURL(storageObject.fileName, storageObject.getURL).catch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        });
      };
    },
    handleFeedEntryClick(action) {
      if (typeof action.onClick === 'undefined') {
        return;
      }

      action.onClick();
    },
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .feed-entry-item {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;

    ._top {
      display: flex;

      ._user {
        display: flex;
        flex: 0 0 5rem;
        justify-content: center;
      }

      ._date {
        font-size: $font-size-4;
        color: $font-color-tertiary;
      }
    }

    ._indicator {
      margin-top: .6rem;
      margin-left: auto;

      ._circle {
        width: .8rem;
        height: .8rem;
        background-color: $primary-color;
        border-radius: .4rem;
      }
    }

    ._payload {
      padding-left: 5rem;

      ._title {
        font-size: $font-size-2;
        color: $font-color-secondary;
      }

      ._fulfilled-msg {
        display: flex;
        flex-direction: row;
        align-items: center;

        ._msg {
          display: flex;
          align-items: center;

          ._text {
            margin-right: 1.2rem;
          }
        }

        ._checkmark {
          display: flex;
          align-items: center;
          margin-right: .8rem;
        }
      }

      ._user {
        font-size: $font-size-4;
        color: $font-color-tertiary;
      }

      ._action {
        margin-right: .6rem;

        ._icon {
          margin-right: 1.2rem;
        }
      }
    }
  }
</style>
